import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, DatePicker, Drawer, Form, Input, InputNumber, Row, Select, Space, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSqlData, insertData } from '../../../services/api';
import StandardTable from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import type { AdmFuelPump, AdmFuelSourceTransfer, AdmVehicle, Company, Personnel } from '../../../types';
import { useTranslation } from 'react-i18next';
import { useGetCompanies, useGetPersonnel } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const FuelTransferList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-US';
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedFuelType, setSelectedFuelType] = useState<string | undefined>(undefined);
  const [selectedSourceType, setSelectedSourceType] = useState<string | undefined>(undefined);
  const [form] = Form.useForm();

  const formSourceType = Form.useWatch('source_type', form);
  const formTargetType = Form.useWatch('target_type', form);
  const formFuelType = Form.useWatch('fuel_type', form);

  const { data: rows = [], isLoading } = useQuery<AdmFuelSourceTransfer[], Error>({
    queryKey: ['fuelSourceTransfers'],
    queryFn: () => fetchSqlData('adm_fuel_source_transfers'),
    staleTime: 60_000,
  });
  const { data: fuelPumps = [] } = useQuery<AdmFuelPump[], Error>({
    queryKey: ['fuelPumps'],
    queryFn: () => fetchSqlData('adm_fuel_pumps'),
    staleTime: 5 * 60_000,
  });
  const { data: vehicles = [] } = useQuery<AdmVehicle[], Error>({
    queryKey: ['machineryVehicles'],
    queryFn: () => fetchSqlData('adm_vehicles'),
    staleTime: 5 * 60_000,
  });
  const { data: personnelResp } = useGetPersonnel();
  const personnel = useMemo<Personnel[]>(() => {
    const arr = Array.isArray(personnelResp) ? personnelResp : (personnelResp as any)?.data || [];
    return arr as Personnel[];
  }, [personnelResp]);
  const { data: companiesResp = [] } = useGetCompanies();
  const companies = useMemo<Company[]>(() => {
    return Array.isArray(companiesResp) ? companiesResp : ((companiesResp as any)?.data || []);
  }, [companiesResp]);

  const fuelTypeOptions = useMemo(
    () => ['Diesel', 'Gasoline', 'LPG', 'Electric', 'Hybrid'].map(value => ({ value, label: value })),
    []
  );

  const sourceTypeOptions = useMemo(
    () => [
      { value: 'main_depot', label: t('machinery.fuel.transfers.sourceTypeMainDepot') },
      { value: 'fixed_pump', label: t('machinery.fuel.transfers.sourceTypeFixedPump') },
      { value: 'fuel_tanker', label: t('machinery.fuel.transfers.sourceTypeFuelTanker') },
      { value: 'external_supplier', label: t('machinery.fuel.transfers.sourceTypeExternalSupplier') },
      { value: 'adjustment', label: t('machinery.fuel.transfers.sourceTypeAdjustment') },
    ],
    [t]
  );

  const targetTypeOptions = useMemo(
    () => [
      { value: 'fixed_pump', label: t('machinery.fuel.transfers.targetTypeFixedPump') },
      { value: 'fuel_tanker', label: t('machinery.fuel.transfers.targetTypeFuelTanker') },
    ],
    [t]
  );

  const activePumps = useMemo(
    () => fuelPumps.filter(pump => pump.status === 'active'),
    [fuelPumps]
  );

  const tankerVehicles = useMemo(
    () => vehicles.filter(vehicle => vehicle.machine_type === 'Fuel Tanker'),
    [vehicles]
  );

  const companyMap = useMemo(() => {
    const map: Record<number, string> = {};
    companies.forEach(company => {
      map[company.id] = company.name;
    });
    return map;
  }, [companies]);

  const personnelMap = useMemo(() => {
    const map: Record<number, string> = {};
    personnel.forEach(person => {
      map[person.id] = `${person.first_name || ''} ${person.last_name || ''}`.trim();
    });
    return map;
  }, [personnel]);

  const pumpMap = useMemo(() => {
    const map: Record<number, string> = {};
    activePumps.forEach(pump => {
      map[pump.id] = [pump.name, pump.location].filter(Boolean).join(' - ');
    });
    return map;
  }, [activePumps]);

  const tankerMap = useMemo(() => {
    const map: Record<number, string> = {};
    tankerVehicles.forEach(vehicle => {
      map[vehicle.id] = `${vehicle.license_plate || '-'} ${vehicle.make || ''} ${vehicle.model || ''}`.trim();
    });
    return map;
  }, [tankerVehicles]);

  const companyOptions = useMemo(
    () => companies.map(company => ({ value: company.id, label: company.name })),
    [companies]
  );

  const personnelOptions = useMemo(
    () => personnel.map(person => ({ value: person.id, label: `${person.first_name || ''} ${person.last_name || ''}`.trim() })),
    [personnel]
  );

  const sourcePumpOptions = useMemo(
    () =>
      activePumps
        .filter(pump => !formFuelType || pump.fuel_type === formFuelType)
        .map(pump => ({ value: pump.id, label: pumpMap[pump.id] || pump.name })),
    [activePumps, formFuelType, pumpMap]
  );

  const targetPumpOptions = useMemo(
    () =>
      activePumps
        .filter(pump => !formFuelType || pump.fuel_type === formFuelType)
        .map(pump => ({ value: pump.id, label: pumpMap[pump.id] || pump.name })),
    [activePumps, formFuelType, pumpMap]
  );

  const sourceTankerOptions = useMemo(
    () =>
      tankerVehicles
        .filter(vehicle => !formFuelType || vehicle.fuel_type === formFuelType)
        .map(vehicle => ({ value: vehicle.id, label: tankerMap[vehicle.id] || String(vehicle.id) })),
    [formFuelType, tankerMap, tankerVehicles]
  );

  const targetTankerOptions = useMemo(
    () =>
      tankerVehicles
        .filter(vehicle => !formFuelType || vehicle.fuel_type === formFuelType)
        .map(vehicle => ({ value: vehicle.id, label: tankerMap[vehicle.id] || String(vehicle.id) })),
    [formFuelType, tankerMap, tankerVehicles]
  );

  useEffect(() => {
    if (formSourceType === 'fixed_pump') {
      form.setFieldsValue({ source_vehicle_id: undefined });
    } else if (formSourceType === 'fuel_tanker') {
      form.setFieldsValue({ source_pump_id: undefined });
    } else {
      form.setFieldsValue({ source_pump_id: undefined, source_vehicle_id: undefined });
    }
  }, [form, formSourceType]);

  useEffect(() => {
    if (formTargetType === 'fixed_pump') {
      form.setFieldsValue({ target_vehicle_id: undefined });
    } else if (formTargetType === 'fuel_tanker') {
      form.setFieldsValue({ target_pump_id: undefined });
    }
  }, [form, formTargetType]);

  const getSourceTypeLabel = (value?: string | null) => {
    if (value === 'main_depot') return t('machinery.fuel.transfers.sourceTypeMainDepot');
    if (value === 'fixed_pump') return t('machinery.fuel.transfers.sourceTypeFixedPump');
    if (value === 'fuel_tanker') return t('machinery.fuel.transfers.sourceTypeFuelTanker');
    if (value === 'external_supplier') return t('machinery.fuel.transfers.sourceTypeExternalSupplier');
    if (value === 'adjustment') return t('machinery.fuel.transfers.sourceTypeAdjustment');
    return '-';
  };

  const getTargetTypeLabel = (value?: string | null) => {
    if (value === 'fixed_pump') return t('machinery.fuel.transfers.targetTypeFixedPump');
    if (value === 'fuel_tanker') return t('machinery.fuel.transfers.targetTypeFuelTanker');
    return '-';
  };

  const getSourceLabel = (row: AdmFuelSourceTransfer) => {
    if (row.source_type === 'fixed_pump') return pumpMap[Number(row.source_pump_id)] || '-';
    if (row.source_type === 'fuel_tanker') return tankerMap[Number(row.source_vehicle_id)] || '-';
    return row.source_name || '-';
  };

  const getTargetLabel = (row: AdmFuelSourceTransfer) => {
    if (row.target_type === 'fixed_pump') return pumpMap[Number(row.target_pump_id)] || '-';
    if (row.target_type === 'fuel_tanker') return tankerMap[Number(row.target_vehicle_id)] || '-';
    return '-';
  };

  const filteredRows = useMemo(() => {
    let nextRows = rows;

    if (selectedFuelType) {
      nextRows = nextRows.filter(row => row.fuel_type === selectedFuelType);
    }

    if (selectedSourceType) {
      nextRows = nextRows.filter(row => row.source_type === selectedSourceType);
    }

    if (search.trim()) {
      const query = search.trim().toLowerCase();
      nextRows = nextRows.filter(row =>
        getSourceLabel(row).toLowerCase().includes(query) ||
        getTargetLabel(row).toLowerCase().includes(query) ||
        (row.reference_no || '').toLowerCase().includes(query) ||
        (row.location || '').toLowerCase().includes(query) ||
        (row.note || '').toLowerCase().includes(query)
      );
    }

    return nextRows;
  }, [rows, selectedFuelType, selectedSourceType, search, pumpMap, tankerMap]);

  const handleAdd = () => {
    form.setFieldsValue({
      transfer_date: dayjs(),
      source_type: 'main_depot',
      target_type: 'fixed_pump',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (
        values.source_type === values.target_type &&
        (
          (values.source_type === 'fixed_pump' && Number(values.source_pump_id) === Number(values.target_pump_id)) ||
          (values.source_type === 'fuel_tanker' && Number(values.source_vehicle_id) === Number(values.target_vehicle_id))
        )
      ) {
        message.error(t('machinery.fuel.transfers.sameSourceTargetError'));
        return;
      }

      const payload = {
        transfer_date: values.transfer_date?.format('YYYY-MM-DD'),
        source_type: values.source_type,
        source_pump_id: values.source_type === 'fixed_pump' ? Number(values.source_pump_id) : null,
        source_vehicle_id: values.source_type === 'fuel_tanker' ? Number(values.source_vehicle_id) : null,
        source_name: ['main_depot', 'external_supplier', 'adjustment'].includes(values.source_type) ? values.source_name?.trim() || null : null,
        target_type: values.target_type,
        target_pump_id: values.target_type === 'fixed_pump' ? Number(values.target_pump_id) : null,
        target_vehicle_id: values.target_type === 'fuel_tanker' ? Number(values.target_vehicle_id) : null,
        fuel_type: values.fuel_type,
        liters: values.liters,
        personnel_id: values.personnel_id ? Number(values.personnel_id) : null,
        company_id: values.company_id ? Number(values.company_id) : null,
        location: values.location?.trim() || null,
        reference_no: values.reference_no?.trim() || null,
        note: values.note?.trim() || null,
      };

      await insertData('adm_fuel_source_transfers', payload);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['fuelSourceTransfers'] }),
        queryClient.invalidateQueries({ queryKey: ['fuelPumps'] }),
      ]);
      message.success(t('machinery.fuel.transfers.addSuccess'));
      handleClose();
    } catch (error: any) {
      message.error(t('machinery.fuel.transfers.operationFailed', { message: error?.response?.data?.error || error?.message || '' }));
    }
  };

  const columns = [
    {
      title: t('machinery.fuel.transfers.date'),
      dataIndex: 'transfer_date',
      key: 'transfer_date',
      width: 120,
      render: (value: string) => (value ? new Date(value).toLocaleDateString(locale) : '-'),
    },
    {
      title: t('machinery.fuel.transfers.sourceType'),
      dataIndex: 'source_type',
      key: 'source_type',
      width: 140,
      render: (value: string) => getSourceTypeLabel(value),
    },
    {
      title: t('machinery.fuel.transfers.source'),
      key: 'source',
      width: 180,
      render: (_: unknown, row: AdmFuelSourceTransfer) => getSourceLabel(row),
      exportValue: (row: AdmFuelSourceTransfer) => getSourceLabel(row),
    },
    {
      title: t('machinery.fuel.transfers.targetType'),
      dataIndex: 'target_type',
      key: 'target_type',
      width: 140,
      render: (value: string) => getTargetTypeLabel(value),
    },
    {
      title: t('machinery.fuel.transfers.target'),
      key: 'target',
      width: 180,
      render: (_: unknown, row: AdmFuelSourceTransfer) => getTargetLabel(row),
      exportValue: (row: AdmFuelSourceTransfer) => getTargetLabel(row),
    },
    {
      title: t('machinery.fuel.transfers.fuelType'),
      dataIndex: 'fuel_type',
      key: 'fuel_type',
      width: 100,
    },
    {
      title: t('machinery.fuel.transfers.liters'),
      dataIndex: 'liters',
      key: 'liters',
      width: 110,
      align: 'right' as const,
      render: (value: number) => value?.toLocaleString(locale, { maximumFractionDigits: 2 }) || '-',
    },
    {
      title: t('machinery.fuel.transfers.personnel'),
      dataIndex: 'personnel_id',
      key: 'personnel_id',
      width: 160,
      render: (value: number | null | undefined) => (value ? personnelMap[value] || '-' : '-'),
      exportValue: (row: AdmFuelSourceTransfer) => (row.personnel_id ? personnelMap[row.personnel_id] || '-' : '-'),
    },
    {
      title: t('machinery.fuel.transfers.company'),
      dataIndex: 'company_id',
      key: 'company_id',
      width: 160,
      render: (value: number | null | undefined) => (value ? companyMap[value] || '-' : '-'),
      exportValue: (row: AdmFuelSourceTransfer) => (row.company_id ? companyMap[row.company_id] || '-' : '-'),
    },
    {
      title: t('machinery.fuel.transfers.referenceNo'),
      dataIndex: 'reference_no',
      key: 'reference_no',
      width: 140,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: t('machinery.fuel.transfers.location'),
      dataIndex: 'location',
      key: 'location',
      width: 140,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: t('machinery.fuel.transfers.note'),
      dataIndex: 'note',
      key: 'note',
      render: (value: string | null | undefined) => value || '-',
    },
  ];

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {t('machinery.fuel.transfers.title')}
          </Title>
        </Col>
        <Col>
          <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {t('machinery.fuel.transfers.addButton')}
          </Button>
        </Col>
      </Row>

      <FilterBar
        items={[
          {
            type: 'text',
            key: 'search',
            placeholder: t('machinery.fuel.transfers.searchPlaceholder'),
            value: search,
            onChange: value => setSearch(value),
            width: 260,
          },
          {
            type: 'select',
            key: 'fuelType',
            placeholder: t('machinery.fuel.transfers.fuelTypePlaceholder'),
            value: selectedFuelType as any,
            onChange: value => setSelectedFuelType(value),
            options: fuelTypeOptions,
            width: 170,
            allowClear: true,
          },
          {
            type: 'select',
            key: 'sourceType',
            placeholder: t('machinery.fuel.transfers.sourceTypePlaceholder'),
            value: selectedSourceType as any,
            onChange: value => setSelectedSourceType(value),
            options: sourceTypeOptions,
            width: 200,
            allowClear: true,
          },
        ]}
        onReset={() => {
          setSearch('');
          setSelectedFuelType(undefined);
          setSelectedSourceType(undefined);
        }}
      />

      <StandardTable
        rowKey="id"
        columns={columns as any}
        dataSource={filteredRows}
        loading={isLoading}
        bordered
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        enableCellTooltips
      />

      <Drawer
        title={t('machinery.fuel.transfers.addTitle')}
        open={open}
        onClose={handleClose}
        width={560}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="transfer_date"
            label={t('machinery.fuel.transfers.date')}
            rules={[{ required: true, message: t('machinery.fuel.transfers.dateRequired') }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="fuel_type"
            label={t('machinery.fuel.transfers.fuelType')}
            rules={[{ required: true, message: t('machinery.fuel.transfers.fuelTypeRequired') }]}
          >
            <Select placeholder={t('machinery.fuel.transfers.fuelTypePlaceholder')} options={fuelTypeOptions} />
          </Form.Item>

          <Form.Item
            name="source_type"
            label={t('machinery.fuel.transfers.sourceType')}
            rules={[{ required: true, message: t('machinery.fuel.transfers.sourceTypeRequired') }]}
          >
            <Select placeholder={t('machinery.fuel.transfers.sourceTypePlaceholder')} options={sourceTypeOptions} />
          </Form.Item>

          {formSourceType === 'fixed_pump' ? (
            <Form.Item
              name="source_pump_id"
              label={t('machinery.fuel.transfers.sourcePump')}
              rules={[{ required: true, message: t('machinery.fuel.transfers.sourcePumpRequired') }]}
            >
              <Select
                showSearch
                placeholder={t('machinery.fuel.transfers.sourcePumpPlaceholder')}
                options={sourcePumpOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          ) : null}

          {formSourceType === 'fuel_tanker' ? (
            <Form.Item
              name="source_vehicle_id"
              label={t('machinery.fuel.transfers.sourceTanker')}
              rules={[{ required: true, message: t('machinery.fuel.transfers.sourceTankerRequired') }]}
            >
              <Select
                showSearch
                placeholder={t('machinery.fuel.transfers.sourceTankerPlaceholder')}
                options={sourceTankerOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          ) : null}

          {['main_depot', 'external_supplier', 'adjustment'].includes(formSourceType || '') ? (
            <Form.Item
              name="source_name"
              label={t('machinery.fuel.transfers.sourceName')}
              rules={[{ required: true, message: t('machinery.fuel.transfers.sourceNameRequired') }]}
            >
              <Input placeholder={t('machinery.fuel.transfers.sourceNamePlaceholder')} />
            </Form.Item>
          ) : null}

          <Form.Item
            name="target_type"
            label={t('machinery.fuel.transfers.targetType')}
            rules={[{ required: true, message: t('machinery.fuel.transfers.targetTypeRequired') }]}
          >
            <Select placeholder={t('machinery.fuel.transfers.targetTypePlaceholder')} options={targetTypeOptions} />
          </Form.Item>

          {formTargetType === 'fixed_pump' ? (
            <Form.Item
              name="target_pump_id"
              label={t('machinery.fuel.transfers.targetPump')}
              rules={[{ required: true, message: t('machinery.fuel.transfers.targetPumpRequired') }]}
            >
              <Select
                showSearch
                placeholder={t('machinery.fuel.transfers.targetPumpPlaceholder')}
                options={targetPumpOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          ) : null}

          {formTargetType === 'fuel_tanker' ? (
            <Form.Item
              name="target_vehicle_id"
              label={t('machinery.fuel.transfers.targetTanker')}
              rules={[{ required: true, message: t('machinery.fuel.transfers.targetTankerRequired') }]}
            >
              <Select
                showSearch
                placeholder={t('machinery.fuel.transfers.targetTankerPlaceholder')}
                options={targetTankerOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          ) : null}

          <Form.Item
            name="liters"
            label={t('machinery.fuel.transfers.liters')}
            rules={[{ required: true, message: t('machinery.fuel.transfers.litersRequired') }]}
          >
            <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="personnel_id" label={t('machinery.fuel.transfers.personnel')}>
            <Select
              showSearch
              allowClear
              placeholder={t('machinery.fuel.transfers.personnelPlaceholder')}
              options={personnelOptions}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item name="company_id" label={t('machinery.fuel.transfers.company')}>
            <Select
              showSearch
              allowClear
              placeholder={t('machinery.fuel.transfers.companyPlaceholder')}
              options={companyOptions}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item name="location" label={t('machinery.fuel.transfers.location')}>
            <Input placeholder={t('machinery.fuel.transfers.locationPlaceholder')} />
          </Form.Item>

          <Form.Item name="reference_no" label={t('machinery.fuel.transfers.referenceNo')}>
            <Input placeholder={t('machinery.fuel.transfers.referenceNoPlaceholder')} />
          </Form.Item>

          <Form.Item name="note" label={t('machinery.fuel.transfers.note')}>
            <Input.TextArea rows={3} placeholder={t('machinery.fuel.transfers.notePlaceholder')} />
          </Form.Item>

          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleClose}>{t('machinery.machines.common.cancel')}</Button>
            <Button action="add" type="primary" onClick={handleSubmit}>
              {t('machinery.machines.common.save')}
            </Button>
          </Space>
        </Form>
      </Drawer>
    </Card>
  );
};

export default FuelTransferList;
